import { create } from 'zustand';
import type {
  Phase,
  PhaseStatus,
  TestReview,
  VideoAnalysisProvider,
} from '@sr-test-wizard/shared';
import { api } from '../lib/api';
import { runAssetsApi, type CriticalFilesResult, type TreeNode } from '../lib/runAssets';
import { texecApi, type TexecViewModel } from '../lib/texecApi';
import { envLinksApi, type EnvLinkBundle } from '../lib/envLinksApi';

export interface RunSummary {
  id: string;
  sr_key: string;
  texec_key: string;
  studio: string;
  game_name: string;
  status: string;
  project_path: string | null;
  images_path: string | null;
  /**
   * Raw TEXEC "Order Type" string captured when the run was created
   * (e.g. "Certification", "Re-certification"). May be missing/null on
   * legacy records the server hasn't backfilled yet.
   */
  order_type?: string | null;
  owner_email?: string | null;
  assignee?: string | null;
  closed_at?: string | null;
  provenance?: {
    gitlab_project_id?: string | null;
    gitlab_branch?: string | null;
    gitlab_commit_sha?: string | null;
    archive_path?: string | null;
    archive_sha256?: string | null;
    archive_bytes?: number | null;
    pulled_at?: string | null;
    drive_images_link?: string | null;
    drive_video_links?: string[];
  };
  artifacts?: {
    files?: string;
    videos?: string;
    images?: string;
    frames?: string;
  };
  phases: Partial<Record<Phase, PhaseStatus>>;
  created_at: string;
  updated_at: string;
}

export type CriticalFilesSource = 'texec' | 'manifest' | 'hashes';

/**
 * Per-run override for the Phase 6 analyser's evidence blocks. `null` means
 * "use whatever exists on disk" (the previous behaviour). The Phase 6
 * toolbar sets this so both the bulk and per-test endpoints see the same
 * choice when the user toggles Images / Video / Both.
 */
export interface AnalysisInputs {
  images: boolean;
  video: boolean;
  provider?: VideoAnalysisProvider;
}

interface RunState {
  current: RunSummary | null;
  tests: TestReview[];
  loading: boolean;
  criticalFiles: CriticalFilesResult | null;
  criticalFilesLoading: boolean;
  criticalFilesError: string | null;
  fileTree: TreeNode | null;
  fileTreeLoading: boolean;
  texec: TexecViewModel | null;
  texecLoading: boolean;
  texecError: string | null;
  envLinks: EnvLinkBundle | null;
  envLinksLoading: boolean;
  envLinksError: string | null;
  analyzingTestKeys: string[];
  /** True while the bulk analyze-all job is running for the current run. */
  analyzeAllRunning: boolean;
  /**
   * Live per-test scout (tier-1) progress. Keyed by test_key, populated by
   * `test-scout-progress` WS events while the source-code review pipeline
   * is mid-flight, and cleared when the matching `test-analyze-status`
   * `completed`/`failed` event arrives. The `TestDetailPane` reads this to
   * show a "Scouting 3/6: <filename>" sub-line under the main "Analyzing
   * TLIB-..." banner.
   */
  scoutProgress: Record<
    string,
    { current: number; total: number; filename: string; status: 'started' | 'completed' | 'cached' | 'failed' }
  >;
  /** Phase 6 evidence toggle. `null` defaults to "use what's on disk". */
  analysisInputs: AnalysisInputs | null;
  loadRun: (id: string) => Promise<void>;
  setRun: (run: RunSummary) => void;
  setPhase: (phase: Phase, status: PhaseStatus) => void;
  applyTestUpdate: (testKey: string, patch: Partial<TestReview>) => void;
  setTestAnalyzing: (testKey: string, analyzing: boolean) => void;
  clearAnalyzing: () => void;
  setAnalyzeAllRunning: (running: boolean) => void;
  setScoutProgress: (
    testKey: string,
    progress:
      | { current: number; total: number; filename: string; status: 'started' | 'completed' | 'cached' | 'failed' }
      | null,
  ) => void;
  loadCriticalFiles: (
    runId: string,
    source: CriticalFilesSource,
    manifestPath?: string,
  ) => Promise<void>;
  clearCriticalFiles: () => void;
  loadFileTree: (runId: string) => Promise<void>;
  loadTexec: (runId: string, options?: { force?: boolean }) => Promise<void>;
  loadEnvLinks: (runId: string, options?: { force?: boolean }) => Promise<void>;
  refreshEnvLinks: (runId: string) => Promise<void>;
  setAnalysisInputs: (inputs: AnalysisInputs | null) => void;
  /**
   * Drop everything we know about the given run from this store. Called when
   * the user (or another tab) deletes the run; the network DELETE has already
   * succeeded by the time this fires.
   */
  removeRunLocally: (runId: string) => void;
}

export const useRunStore = create<RunState>((set, get) => ({
  current: null,
  tests: [],
  loading: false,
  criticalFiles: null,
  criticalFilesLoading: false,
  criticalFilesError: null,
  fileTree: null,
  fileTreeLoading: false,
  texec: null,
  texecLoading: false,
  texecError: null,
  envLinks: null,
  envLinksLoading: false,
  envLinksError: null,
  analyzingTestKeys: [],
  analyzeAllRunning: false,
  scoutProgress: {},
  analysisInputs: null,
  async loadRun(id) {
    set({ loading: true });
    try {
      const [run, tests] = await Promise.all([
        api.get<RunSummary>(`/api/runs/${id}`),
        api.get<TestReview[]>(`/api/runs/${id}/tests`),
      ]);
      const previous = get().current;
      // Only treat this as a "different run" if we actually had a previous
      // run id and it changed. The "no previous" branch used to clear caches
      // here too, but that races with parallel preloads (e.g. loadTexec on
      // dashboard mount) and would wipe freshly-loaded data.
      const isDifferentRun = !!previous && previous.id !== run.id;
      set({
        current: run,
        tests,
        criticalFiles: isDifferentRun ? null : get().criticalFiles,
        fileTree: isDifferentRun ? null : get().fileTree,
        criticalFilesError: isDifferentRun ? null : get().criticalFilesError,
        texec: isDifferentRun ? null : get().texec,
        texecError: isDifferentRun ? null : get().texecError,
        envLinks: isDifferentRun ? null : get().envLinks,
        envLinksError: isDifferentRun ? null : get().envLinksError,
        analyzingTestKeys: isDifferentRun ? [] : get().analyzingTestKeys,
        analyzeAllRunning: isDifferentRun ? false : get().analyzeAllRunning,
        scoutProgress: isDifferentRun ? {} : get().scoutProgress,
        analysisInputs: isDifferentRun ? null : get().analysisInputs,
      });
    } finally {
      set({ loading: false });
    }
  },
  setRun(run) {
    set({ current: run });
  },
  setPhase(phase, status) {
    const cur = get().current;
    if (!cur) return;
    set({ current: { ...cur, phases: { ...cur.phases, [phase]: status } } });
  },
  applyTestUpdate(testKey, patch) {
    set((state) => ({
      tests: state.tests.map((t) => (t.test_key === testKey ? { ...t, ...patch } : t)),
    }));
  },
  setTestAnalyzing(testKey, analyzing) {
    set((state) => {
      const has = state.analyzingTestKeys.includes(testKey);
      if (analyzing && !has) {
        return { analyzingTestKeys: [...state.analyzingTestKeys, testKey] };
      }
      if (!analyzing && has) {
        return { analyzingTestKeys: state.analyzingTestKeys.filter((k) => k !== testKey) };
      }
      return state;
    });
  },
  clearAnalyzing() {
    set({ analyzingTestKeys: [], scoutProgress: {}, analyzeAllRunning: false });
  },
  setAnalyzeAllRunning(running) {
    set({ analyzeAllRunning: running });
  },
  setScoutProgress(testKey, progress) {
    set((state) => {
      const next = { ...state.scoutProgress };
      if (progress === null) {
        delete next[testKey];
      } else {
        next[testKey] = progress;
      }
      return { scoutProgress: next };
    });
  },
  setAnalysisInputs(inputs) {
    set({ analysisInputs: inputs });
  },
  async loadCriticalFiles(runId, source, manifestPath) {
    set({ criticalFilesLoading: true, criticalFilesError: null });
    try {
      const data =
        source === 'texec'
          ? await runAssetsApi.loadCriticalFilesFromTexec(runId)
          : await runAssetsApi.loadCriticalFilesFromManifest(runId, manifestPath ?? '');
      set({ criticalFiles: data });
    } catch (err) {
      set({ criticalFilesError: (err as Error).message });
    } finally {
      set({ criticalFilesLoading: false });
    }
  },
  clearCriticalFiles() {
    set({ criticalFiles: null, criticalFilesError: null });
  },
  async loadFileTree(runId) {
    set({ fileTreeLoading: true });
    try {
      const tree = await runAssetsApi.loadFileTree(runId);
      set({ fileTree: tree });
    } finally {
      set({ fileTreeLoading: false });
    }
  },
  async loadTexec(runId, options) {
    const force = options?.force ?? false;
    const state = get();
    if (!force && state.texec && state.current?.id === runId) return;
    if (state.texecLoading) return;
    set({ texecLoading: true, texecError: null });
    try {
      const data = await texecApi.load(runId);
      set({ texec: data });
    } catch (err) {
      set({ texecError: (err as Error).message });
    } finally {
      set({ texecLoading: false });
    }
  },
  async loadEnvLinks(runId, options) {
    const force = options?.force ?? false;
    const state = get();
    if (!force && state.envLinks && state.current?.id === runId) return;
    if (state.envLinksLoading) return;
    set({ envLinksLoading: true, envLinksError: null });
    try {
      const data = await envLinksApi.load(runId);
      set({ envLinks: data });
    } catch (err) {
      set({ envLinksError: (err as Error).message });
    } finally {
      set({ envLinksLoading: false });
    }
  },
  async refreshEnvLinks(runId) {
    set({ envLinksLoading: true, envLinksError: null });
    try {
      const data = await envLinksApi.refresh(runId);
      set({ envLinks: data });
    } catch (err) {
      set({ envLinksError: (err as Error).message });
    } finally {
      set({ envLinksLoading: false });
    }
  },
  removeRunLocally(runId) {
    const state = get();
    if (state.current?.id !== runId) return;
    set({
      current: null,
      tests: [],
      criticalFiles: null,
      criticalFilesError: null,
      fileTree: null,
      texec: null,
      texecError: null,
      envLinks: null,
      envLinksError: null,
      analyzingTestKeys: [],
      analyzeAllRunning: false,
      scoutProgress: {},
      analysisInputs: null,
    });
  },
}));
